// src/components/ScrollTabs/ScrollTabs.jsx
"use client";

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";

import "./ScrollTabs.css";

/* ==== Icônes stroke-only ==== */
const IconWrap = ({ className = "", children }) => (
  <span className={`st-icon ${className}`} aria-hidden="true">
    <svg viewBox="0 0 24 24">{children}</svg>
  </span>
);

const GridIcon = () => (
  <IconWrap>
    <rect x="4" y="4" width="6" height="6" rx="1.5" />
    <rect x="14" y="4" width="6" height="6" rx="1.5" />
    <rect x="4" y="14" width="6" height="6" rx="1.5" />
    <rect x="14" y="14" width="6" height="6" rx="1.5" />
  </IconWrap>
);

const FolderIcon = () => (
  <IconWrap>
    <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6H9l2 2.5h8A1.5 1.5 0 0 1 20.5 10v7A1.5 1.5 0 0 1 19 18.5H5A2 2 0 0 1 3 16.5v-9Z" />
  </IconWrap>
);

const CapsuleIcon = () => (
  <IconWrap className="st-icon--pill st-icon--big">
    <g transform="rotate(-28 12 12)">
      <rect x="5" y="7" width="14" height="10" rx="5" />
      <line x1="12" y1="7" x2="12" y2="17" />
    </g>
  </IconWrap>
);

const UserIcon = () => (
  <IconWrap>
    <path d="M12 12a4 4 0 1 0-0.001-8.001A4 4 0 0 0 12 12Z" />
    <path d="M5 19.5a7 7 0 0 1 14 0" />
  </IconWrap>
);

/**
 * Props:
 * - mode: "sticky" | "fixed" (default: "sticky")
 * - userId: string
 * - headerOffset: number (default 64)
 *
 * ✅ NEW:
 * - activeKey?: string (contrôlé depuis parent)
 * - onChange?: (key:string)=>void (contrôlé depuis parent)
 * - forceActiveKey?: string (force un onglet actif pour les sous-pages)
 */
export default function ScrollTabs({
  mode = "sticky",
  userId,
  headerOffset = 64,
  activeKey: activeKeyProp,
  onChange,
  forceActiveKey,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const profileKey = useMemo(
    () => ROUTES.PROFILE(userId || ":userId"),
    [userId]
  );

  const tabs = useMemo(
    () => [
      { 
        key: ROUTES.OVERVIEWS, 
        icon: <GridIcon />, 
        label: t('scrollTabs.overview') 
      },
      { 
        key: ROUTES.MEDICAL_RECORD, 
        icon: <FolderIcon />, 
        label: t('scrollTabs.yourRecords') 
      },
      { 
        key: ROUTES.MEDICINES, 
        icon: <CapsuleIcon />, 
        label: t('scrollTabs.medicine') 
      },
      { 
        key: profileKey, 
        icon: <UserIcon />, 
        label: t('scrollTabs.profile') 
      },
    ],
    [profileKey, t]
  );

  // ✅ calcul par défaut selon URL
  const computedActiveKey = useMemo(() => {
    const p = (pathname || "").toLowerCase();

    if ((ROUTES.OVERVIEWS || "").toLowerCase() && p.startsWith(ROUTES.OVERVIEWS.toLowerCase()))
      return ROUTES.OVERVIEWS;

    if ((ROUTES.MEDICAL_RECORD || "").toLowerCase() && p.startsWith(ROUTES.MEDICAL_RECORD.toLowerCase()))
      return ROUTES.MEDICAL_RECORD;

    if ((ROUTES.MEDICINES || "").toLowerCase() && p.startsWith(ROUTES.MEDICINES.toLowerCase()))
      return ROUTES.MEDICINES;

    // toutes les sous-pages profile
    if (p.startsWith("/profile")) return profileKey;

    return ROUTES.OVERVIEWS;
  }, [pathname, profileKey]);

  // ✅ priorité: forceActiveKey > activeKeyProp > computed
  const activeKey = forceActiveKey || activeKeyProp || computedActiveKey;

  const handleTabClick = (key) => {
    if (onChange) return onChange(key);
    navigate(key);
  };

  return (
    <>
      <nav
        className={[
          "scrolltabs",
          mode === "fixed" ? "scrolltabs--fixed" : "scrolltabs--sticky",
        ].join(" ")}
        style={{ "--header-offset": `${headerOffset}px` }}
        role="tablist"
        aria-label={t('scrollTabs.ariaLabel')}
      >
        <ul className="st-list">
          {tabs.map((t) => {
            const isActive = t.key === activeKey;
            return (
              <li key={t.key} className="st-item">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`st-btn ${isActive ? "is-active" : ""}`}
                  onClick={() => handleTabClick(t.key)}
                >
                  <span className="scrolltabs__label">
                    {t.icon}
                    <span>{t.label}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {mode === "fixed" && <div className="scrolltabs-spacer" aria-hidden />}
    </>
  );
}